import React, { useState, useImperativeHandle, forwardRef } from 'react'
import { useFloating, offset, flip, shift, autoUpdate, useClick, useDismiss, useRole, useInteractions, FloatingPortal, FloatingFocusManager } from '@floating-ui/react'
import { galleryIconWeb, pdfIconWeb } from 'Assets/Icons'

const AttachmentMenu = forwardRef((props, ref) => {
  const { placement = 'bottom-end', setShowRedirectText = false, ...handlers } = props

  const [open, setOpen] = useState(false)

  const { refs, context, x, y, strategy } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    strategy: 'fixed',
    middleware: [
      offset({ mainAxis: 4, crossAxis: 6 }), // ⬅️ gap downwards + sideways
      flip(),
      shift({ padding: 8 })
    ],
    whileElementsMounted: autoUpdate
  })

  const click = useClick(context)
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' })
  const role = useRole(context, { role: 'menu' })
  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role])

  const { onClick: _refOnClick, ...referenceProps } = getReferenceProps({
    type: 'button',
    className: 'attachment-menu-trigger',
    'aria-label': 'Attachment'
  })

  const handleTriggerClick = (e) => {
    // if a setter function was passed (guest), show redirect text and do NOT open the menu
    if (typeof setShowRedirectText === 'function') {
      setShowRedirectText(true)

      return
    }

    // otherwise call the floating-ui reference onClick to open the menu
    _refOnClick?.(e)
  }

  useImperativeHandle(
    ref,
    () => ({
      triggerMediaInput: () => mediaInputRef?.click?.(),
      triggerDocInput: () => docInputRef?.click?.()
    }),
    []
  )

  const close = () => setOpen(false)
  const Item = ({ icon, label, onClick, danger, imageUrl }) => (
    <button
      className={`msg-menu__item${danger ? ' danger' : ''}`}
      role="menuitem"
      onClick={() => {
        onClick && onClick()
        close()
      }}
    >
      {imageUrl && <img src={imageUrl} alt={label} className="attachment-menu-item-image" />}
      {icon && <i className={`pi ${icon} ${label === 'Reply' ? 'reply-icon' : ''}`} aria-hidden="true" />}
      <span>{label}</span>
    </button>
  )

  const mediaInputRef = React.createRef()
  const docInputRef = React.createRef()

  const onMediaChange = (e) => {
    const files = Array.from(e.target.files || [])

    handlers.onMediaSelected && handlers.onMediaSelected(files)
    e.target.value = ''
  }

  const onDocChange = (e) => {
    const files = Array.from(e.target.files || [])

    handlers.onDocumentSelected && handlers.onDocumentSelected(files)
    e.target.value = ''
  }

  return (
    <>
      <button ref={refs.setReference} {...referenceProps} onClick={handleTriggerClick}>
        <i className="pi pi-plus" />
      </button>

      <input ref={mediaInputRef} type="file" accept="image/jpeg,image/png,video/mp4,video/quicktime" multiple style={{ display: 'none' }} onChange={onMediaChange} />
      <input ref={docInputRef} type="file" accept=".pdf" multiple style={{ display: 'none' }} onChange={onDocChange} />

      <FloatingPortal>
        {open && (
          <FloatingFocusManager context={context} modal={false} initialFocus={-1}>
            <div
              ref={refs.setFloating}
              {...getFloatingProps({
                className: 'msg-menu',
                style: {
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                  visibility: x == null ? 'hidden' : 'visible'
                }
              })}
            >
              <Item imageUrl={galleryIconWeb} label="Photos and Videos" onClick={() => mediaInputRef.current?.click()} />
              <Item imageUrl={pdfIconWeb} label="PDF" onClick={() => docInputRef.current?.click()} />
              <div className="msg-menu__separator" />
            </div>
          </FloatingFocusManager>
        )}
      </FloatingPortal>
    </>
  )
})

AttachmentMenu.displayName = 'AttachmentMenu'

export default AttachmentMenu
